import { DIST_EPSILON } from "geom/core";
import { intersectLine2Ray2 } from "geom/intersectRay2";
import { line2IsVertical, type Line2Segment } from "geom/line2";
import { offsetPolyLine2SSkFallback } from "geom/offset";
import {
  polyLine2AABB2,
  polyLine2Close,
  polyLine2Copy,
  polyLine2CopySimplify,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2FromAABB2,
  polyLine2IsClosed,
  polyLine2SetCCW,
  polyLine2Shift,
  polyLine2Simplify,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { Rect2 } from "geom/rect2Types";

/**
 * assumes there is only one bottom edge
 * @todo(Andrew): change the siguature to return an index or Null
 */
export function polyLine2MinYEdgeIndex(profile2: PolyLine2, tol: number = DIST_EPSILON): number {
  if (profile2.length < 8) {
    return -1;
  }
  if (!polyLine2IsClosed(profile2)) {
    return -1;
  }
  const edgeCount = polyLine2EdgeCount(profile2);
  const { minY } = polyLine2AABB2(profile2);
  for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex++) {
    const [, y0, , y1] = polyLine2Edge(profile2, edgeIndex) as unknown as Line2Segment;
    if (y0 - tol < minY && y1 - tol < minY) {
      return edgeIndex;
    }
  }
  return -1;
}

/**
 * assumes there is only one bottom edge
 * @todo(Andrew): change the siguature to return an index or Null
 */
export function polyLine2MinXEdgeIndex(profile2: PolyLine2, tol: number = DIST_EPSILON): number {
  const { minX } = polyLine2AABB2(profile2);
  const edgeCount = polyLine2EdgeCount(profile2);
  for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex++) {
    const [x0, , x1] = polyLine2Edge(profile2, edgeIndex) as unknown as Line2Segment;
    if (x0 - tol < minX && x1 - tol < minX) {
      return edgeIndex;
    }
  }
  return -1;
}

/**
 * orient (by shifting) a PolyLine2 so that the bottom edge is the first edge
 */
export function profile2OrientCopy(pl: PolyLine2): PolyLine2 {
  pl = polyLine2Simplify(pl);
  const bottomEdgeIndex = polyLine2MinYEdgeIndex(pl);
  // it is possible for the profile to have no bottom edge,
  // in which case it is non-orientable in that orienting it
  // would be arbitrary
  if (bottomEdgeIndex < 0) {
    return pl;
  }
  if (bottomEdgeIndex === 0) {
    return pl;
  }
  return polyLine2Shift(polyLine2Copy(pl), -bottomEdgeIndex);
}

export function rectProfile2(pl: PolyLine2): Rect2 {
  return profile2OrientCopy(polyLine2FromAABB2(polyLine2AABB2(pl))) as Rect2;
}

export function profile2Offset(profile2: PolyLine2, depth: number, excludeBottom: boolean = true): PolyLine2 {
  // rotate the profile so that the bottom edge is the first edge and remove it
  const copy = profile2OrientCopy(polyLine2SetCCW(polyLine2CopySimplify(profile2)));

  const shouldExcludeBottom = excludeBottom && polyLine2HasBottomEdge(copy);

  const toOffset = shouldExcludeBottom ? ensurePolyLine2HasVerticalEnds(copy.slice(2)) : copy;

  const { minY } = polyLine2AABB2(copy);

  let offset = offsetPolyLine2SSkFallback(toOffset, depth);

  if (!offset || offset.length < 4) {
    return polyLine2New([]);
  }

  if (shouldExcludeBottom) {
    // determine if the offset polyLine extends below the original profile2
    const clippedPoints: number[] = [];
    for (let edgeIndex = 0; edgeIndex < polyLine2EdgeCount(offset); edgeIndex++) {
      const [x0, y0, x1, y1] = polyLine2Edge(offset, edgeIndex) as unknown as Line2Segment;
      if (y0 >= minY && y1 >= minY) {
        clippedPoints.push(x0, y0, x1, y1);
        continue;
      }
      if (y0 < minY && y1 < minY) {
        continue;
      }
      const int = intersectLine2Ray2(x0, y0, x1, y1, x0 - 1, minY, 1, 0);
      if (!int) {
        continue;
      }
      if (y0 < minY) {
        clippedPoints.push(int.x, int.y, x1, y1);
        continue;
      }
      if (y1 < minY) {
        clippedPoints.push(x0, y0, int.x, int.y);
        continue;
      }
    }
    offset = polyLine2Close(polyLine2New(clippedPoints));
  }

  return profile2OrientCopy(polyLine2SetCCW(offset));
}

export function polyLine2HasBottomEdge(pl: PolyLine2): boolean {
  return polyLine2MinYEdgeIndex(pl) >= 0;
}

/**
 * it is possible that offsetting the arcCurve removes the vertical segements attached
 * to each end of the arc; if this happens, we need to re-add them
 */
export function ensurePolyLine2HasVerticalEnds(pl: PolyLine2, verticalOffset: number = 0.01): PolyLine2 {
  const start = polyLine2Edge(pl, 0) as unknown as Line2Segment;
  const startIsVertical = line2IsVertical(...start);

  const end = polyLine2Edge(pl, polyLine2EdgeCount(pl) - 1) as unknown as Line2Segment;
  const endIsVertical = line2IsVertical(...end);

  if (endIsVertical && startIsVertical) {
    return pl;
  }

  const newPts: number[] = [];

  if (!startIsVertical) {
    newPts.push(start[0], start[1] - verticalOffset);
  }
  newPts.push(...pl);
  if (!endIsVertical) {
    newPts.push(end[2], end[3] - verticalOffset);
  }
  return polyLine2New(newPts);
}
